// ================================
// IMPORTS
// ================================
import { computed, nextTick, ref, type Ref, watch } from "vue"
import type { ThresholdPoint } from "@modules/energy-manager/types/alarme"
import {
    ALARM_THRESHOLD_DEFAULT_END_NIGHT,
    ALARM_THRESHOLD_DEFAULT_START_NIGHT,
    getHourlyPointValues,
    getIsLegacyWeekendDay,
    getIsLegacyWeekendHour,
    getIsLegacyNightHour,
} from "@modules/energy-manager/utils/alarmThresholdFormLegacy"
import { inferAlarmThresholdTogglesFromPoints } from "@modules/energy-manager/utils/inferAlarmThresholdFormFromPoints"

// ================================
// TYPES & INTERFACES
// ================================
type ThresholdTripletRefs = {
    consigne: Ref<number>
    limhaute: Ref<number>
    limbasse: Ref<number>
}

// ================================
// COMPOSABLE
// ================================
/**
 * État formulaire seuils (profil uniforme, semaine / week-end, nuit) et synchro
 * avec la série de points parent.
 *
 * @param points série courante (effet serveur / parent)
 * @param onChangePoints callback équivalent `emit('change-points', …)`
 * @returns refs, computed, watchers et `rebuildAndEmitPoints` pour le template
 */
export function useAlarmThresholdForm(
    points: Ref<ThresholdPoint[]>,
    onChangePoints: (payload: ThresholdPoint[]) => void
) {
    /** Évite expand/collapse / copie nuit / rebuild lors de l’inférence initiale depuis les points. */
    const suppressFormSideEffects = ref(false)
    /** Longueur de série pour laquelle l’inférence a été appliquée (re-infère si la longueur change). */
    const inferredForPointsLength = ref<number | null>(null)

    // ================================
    // STATE — mode
    // ================================
    const isDistinguishWeekend = ref(false)

    // ================================
    // STATE — profil uniforme (toggle « Non »)
    // ================================
    const consigne = ref(0)
    const limhaute = ref(0)
    const limbasse = ref(0)

    // ================================
    // STATE — période semaine (toggle « Oui »)
    // ================================
    const weekConsigne = ref(0)
    const weekLimhaute = ref(0)
    const weekLimbasse = ref(0)

    // ================================
    // STATE — week-end (toggle « Oui »)
    // ================================
    const weekendConsigne = ref(0)
    const weekendLimhaute = ref(0)
    const weekendLimbasse = ref(0)

    // ================================
    // STATE — nuit profil uniforme (168 h)
    // ================================
    const isUniformNight = ref(false)
    const uniformNightConsigne = ref(0)
    const uniformNightLimhaute = ref(0)
    const uniformNightLimbasse = ref(0)
    const uniformStartNight = ref(ALARM_THRESHOLD_DEFAULT_START_NIGHT)
    const uniformEndNight = ref(ALARM_THRESHOLD_DEFAULT_END_NIGHT)

    // ================================
    // STATE — nuit semaine (mode distingué, 168 h)
    // ================================
    const weekIsNight = ref(false)
    const weekNightConsigne = ref(0)
    const weekNightLimhaute = ref(0)
    const weekNightLimbasse = ref(0)
    const weekStartNight = ref(ALARM_THRESHOLD_DEFAULT_START_NIGHT)
    const weekEndNight = ref(ALARM_THRESHOLD_DEFAULT_END_NIGHT)

    // ================================
    // STATE — nuit week-end (mode distingué, 168 h)
    // ================================
    const weekendIsNight = ref(false)
    const weekendNightConsigne = ref(0)
    const weekendNightLimhaute = ref(0)
    const weekendNightLimbasse = ref(0)
    const weekendStartNight = ref(ALARM_THRESHOLD_DEFAULT_START_NIGHT)
    const weekendEndNight = ref(ALARM_THRESHOLD_DEFAULT_END_NIGHT)

    // ================================
    // COMPUTED
    // ================================
    /** Nombre de pas dans la série éditée. */
    const getPointsLength = computed(() => points.value.length)

    /** Série horaire 168 pas. */
    const getIsHourlySeries = computed(() => points.value.length === 168)

    /** Affiche le toggle semaine / week-end (7 ou 168 pas). */
    const getShowDistinguishToggle = computed(
        () => points.value.length === 168 || points.value.length === 7
    )

    /** Options select « début de nuit » (17h…23h). */
    const getStartNightSelectOptions = computed(() =>
        Array.from({ length: 7 }, (_, i) => {
            const value = 17 + i
            return { label: `${value}h`, value }
        })
    )

    /** Options select « fin de nuit » (4h…9h). */
    const getEndNightSelectOptions = computed(() =>
        Array.from({ length: 6 }, (_, i) => {
            const value = 4 + i
            return { label: `${value}h`, value }
        })
    )

    /** Formulaire considéré valide si au moins une consigne / limite est non nulle. */
    const getIsValidThresholdForm = computed(() => {
        if (getPointsLength.value === 0) return false

        const nightHasValue = (c: number, h: number, b: number): boolean =>
            c !== 0 || h !== 0 || b !== 0

        if (!isDistinguishWeekend.value) {
            const dayOk =
                consigne.value !== 0 ||
                limhaute.value !== 0 ||
                limbasse.value !== 0
            if (!getIsHourlySeries.value || !isUniformNight.value) return dayOk
            return (
                dayOk ||
                nightHasValue(
                    uniformNightConsigne.value,
                    uniformNightLimhaute.value,
                    uniformNightLimbasse.value
                )
            )
        }

        const weekDayOk =
            weekConsigne.value !== 0 ||
            weekLimhaute.value !== 0 ||
            weekLimbasse.value !== 0
        const weekendDayOk =
            weekendConsigne.value !== 0 ||
            weekendLimhaute.value !== 0 ||
            weekendLimbasse.value !== 0

        if (!getIsHourlySeries.value) {
            return weekDayOk || weekendDayOk
        }

        const weekOk =
            weekDayOk ||
            (weekIsNight.value &&
                nightHasValue(
                    weekNightConsigne.value,
                    weekNightLimhaute.value,
                    weekNightLimbasse.value
                ))
        const weekendOk =
            weekendDayOk ||
            (weekendIsNight.value &&
                nightHasValue(
                    weekendNightConsigne.value,
                    weekendNightLimhaute.value,
                    weekendNightLimbasse.value
                ))

        return weekOk || weekendOk
    })

    // ================================
    // BUSINESS LOGIC — synchro points ⟷ scalaires
    // ================================
    /**
     * Recopie les champs depuis un point vers trois refs.
     *
     * @param target refs consigne / limhaute / limbasse
     * @param point source
     */
    function applyPointToTriplet(
        target: ThresholdTripletRefs,
        point: ThresholdPoint
    ) {
        target.consigne.value = point.consigne
        target.limhaute.value = point.limhaute
        target.limbasse.value = point.limbasse
    }

    /** Mercredi 12 h (timestamp 84) ou premier jour ouvré à midi. */
    function pickWeekdayDayBaseline(pts: ThresholdPoint[]): ThresholdPoint {
        return (
            pts.find((p) => p.timestamp === 84) ??
            pts.find(
                (p) =>
                    !getIsLegacyWeekendHour(p.timestamp) &&
                    p.timestamp % 24 === 12
            ) ??
            pts[84]
        )
    }

    /** Premier point week-end à midi (dim. puis sam.). */
    function pickWeekendDayBaseline(pts: ThresholdPoint[]): ThresholdPoint {
        return (
            pts.find(
                (p) =>
                    getIsLegacyWeekendHour(p.timestamp) &&
                    p.timestamp % 24 === 12
            ) ?? pts[12]
        )
    }

    function pickWeekdayDayPointForWindow(
        pts: ThresholdPoint[],
        startNight: number,
        endNight: number
    ): ThresholdPoint | undefined {
        return pts.find(
            (p) =>
                !getIsLegacyWeekendHour(p.timestamp) &&
                !getIsLegacyNightHour(p.timestamp % 24, startNight, endNight)
        )
    }

    function pickWeekdayNightPointForWindow(
        pts: ThresholdPoint[],
        startNight: number,
        endNight: number
    ): ThresholdPoint | undefined {
        return pts.find(
            (p) =>
                !getIsLegacyWeekendHour(p.timestamp) &&
                getIsLegacyNightHour(p.timestamp % 24, startNight, endNight)
        )
    }

    function pickWeekendDayPointForWindow(
        pts: ThresholdPoint[],
        startNight: number,
        endNight: number
    ): ThresholdPoint | undefined {
        return pts.find(
            (p) =>
                getIsLegacyWeekendHour(p.timestamp) &&
                !getIsLegacyNightHour(p.timestamp % 24, startNight, endNight)
        )
    }

    function pickWeekendNightPointForWindow(
        pts: ThresholdPoint[],
        startNight: number,
        endNight: number
    ): ThresholdPoint | undefined {
        return pts.find(
            (p) =>
                getIsLegacyWeekendHour(p.timestamp) &&
                getIsLegacyNightHour(p.timestamp % 24, startNight, endNight)
        )
    }

    function copyUniformDayToNight() {
        uniformNightConsigne.value = consigne.value
        uniformNightLimhaute.value = limhaute.value
        uniformNightLimbasse.value = limbasse.value
    }

    function copyWeekDayToNight() {
        weekNightConsigne.value = weekConsigne.value
        weekNightLimhaute.value = weekLimhaute.value
        weekNightLimbasse.value = weekLimbasse.value
    }

    function copyWeekendDayToNight() {
        weekendNightConsigne.value = weekendConsigne.value
        weekendNightLimhaute.value = weekendLimhaute.value
        weekendNightLimbasse.value = weekendLimbasse.value
    }

    function resetNightState() {
        isUniformNight.value = false
        uniformNightConsigne.value = 0
        uniformNightLimhaute.value = 0
        uniformNightLimbasse.value = 0
        uniformStartNight.value = ALARM_THRESHOLD_DEFAULT_START_NIGHT
        uniformEndNight.value = ALARM_THRESHOLD_DEFAULT_END_NIGHT

        weekIsNight.value = false
        weekNightConsigne.value = 0
        weekNightLimhaute.value = 0
        weekNightLimbasse.value = 0
        weekStartNight.value = ALARM_THRESHOLD_DEFAULT_START_NIGHT
        weekEndNight.value = ALARM_THRESHOLD_DEFAULT_END_NIGHT

        weekendIsNight.value = false
        weekendNightConsigne.value = 0
        weekendNightLimhaute.value = 0
        weekendNightLimbasse.value = 0
        weekendStartNight.value = ALARM_THRESHOLD_DEFAULT_START_NIGHT
        weekendEndNight.value = ALARM_THRESHOLD_DEFAULT_END_NIGHT
    }

    /**
     * Réinjecte les champs du formulaire depuis la liste de points parent.
     *
     * @param pts points reçus du parent
     */
    function syncScalarsFromPoints(pts: ThresholdPoint[]) {
        if (!pts.length) {
            consigne.value = 0
            limhaute.value = 0
            limbasse.value = 0
            weekConsigne.value = 0
            weekLimhaute.value = 0
            weekLimbasse.value = 0
            weekendConsigne.value = 0
            weekendLimhaute.value = 0
            weekendLimbasse.value = 0
            resetNightState()
            return
        }

        if (!isDistinguishWeekend.value) {
            if (pts.length === 168) {
                const dayPt =
                    pts.find(
                        (p) =>
                            !getIsLegacyWeekendHour(p.timestamp) &&
                            p.timestamp % 24 === 12
                    ) ?? pickWeekdayDayBaseline(pts)
                applyPointToTriplet(
                    {
                        consigne,
                        limhaute,
                        limbasse,
                    },
                    dayPt
                )
                if (isUniformNight.value) {
                    const nightPt = pts.find(
                        (p) =>
                            !getIsLegacyWeekendHour(p.timestamp) &&
                            getIsLegacyNightHour(
                                p.timestamp % 24,
                                uniformStartNight.value,
                                uniformEndNight.value
                            )
                    )
                    if (nightPt) {
                        applyPointToTriplet(
                            {
                                consigne: uniformNightConsigne,
                                limhaute: uniformNightLimhaute,
                                limbasse: uniformNightLimbasse,
                            },
                            nightPt
                        )
                    }
                }
                return
            }
            applyPointToTriplet(
                {
                    consigne,
                    limhaute,
                    limbasse,
                },
                pts[0]
            )
            return
        }

        if (pts.length === 7) {
            const wk = pts.find((_, i) => !getIsLegacyWeekendDay(i)) ?? pts[1]
            const we = pts.find((_, i) => getIsLegacyWeekendDay(i)) ?? pts[0]
            applyPointToTriplet(
                {
                    consigne: weekConsigne,
                    limhaute: weekLimhaute,
                    limbasse: weekLimbasse,
                },
                wk
            )
            applyPointToTriplet(
                {
                    consigne: weekendConsigne,
                    limhaute: weekendLimhaute,
                    limbasse: weekendLimbasse,
                },
                we
            )
            return
        }

        if (pts.length === 168) {
            if (weekIsNight.value) {
                const wkDay =
                    pickWeekdayDayPointForWindow(
                        pts,
                        weekStartNight.value,
                        weekEndNight.value
                    ) ?? pickWeekdayDayBaseline(pts)
                applyPointToTriplet(
                    {
                        consigne: weekConsigne,
                        limhaute: weekLimhaute,
                        limbasse: weekLimbasse,
                    },
                    wkDay
                )
                const weekNightPt = pickWeekdayNightPointForWindow(
                    pts,
                    weekStartNight.value,
                    weekEndNight.value
                )
                if (weekNightPt) {
                    applyPointToTriplet(
                        {
                            consigne: weekNightConsigne,
                            limhaute: weekNightLimhaute,
                            limbasse: weekNightLimbasse,
                        },
                        weekNightPt
                    )
                }
            } else {
                const wk = pickWeekdayDayBaseline(pts)
                applyPointToTriplet(
                    {
                        consigne: weekConsigne,
                        limhaute: weekLimhaute,
                        limbasse: weekLimbasse,
                    },
                    wk
                )
            }

            if (weekendIsNight.value) {
                const weDay =
                    pickWeekendDayPointForWindow(
                        pts,
                        weekendStartNight.value,
                        weekendEndNight.value
                    ) ?? pickWeekendDayBaseline(pts)
                applyPointToTriplet(
                    {
                        consigne: weekendConsigne,
                        limhaute: weekendLimhaute,
                        limbasse: weekendLimbasse,
                    },
                    weDay
                )
                const weekendNightPt = pickWeekendNightPointForWindow(
                    pts,
                    weekendStartNight.value,
                    weekendEndNight.value
                )
                if (weekendNightPt) {
                    applyPointToTriplet(
                        {
                            consigne: weekendNightConsigne,
                            limhaute: weekendNightLimhaute,
                            limbasse: weekendNightLimbasse,
                        },
                        weekendNightPt
                    )
                }
            } else {
                const we = pickWeekendDayBaseline(pts)
                applyPointToTriplet(
                    {
                        consigne: weekendConsigne,
                        limhaute: weekendLimhaute,
                        limbasse: weekendLimbasse,
                    },
                    we
                )
            }
        }
    }

    /**
     * Reconstruit la série complète et notifie le parent.
     */
    function rebuildAndEmitPoints() {
        const n = points.value.length
        if (n === 0) return

        if (!isDistinguishWeekend.value) {
            if (n === 168 && isUniformNight.value) {
                const next = Array.from({ length: 168 }, (_, index) => {
                    const v = getHourlyPointValues(
                        index,
                        consigne.value,
                        limhaute.value,
                        limbasse.value,
                        true,
                        uniformNightConsigne.value,
                        uniformNightLimhaute.value,
                        uniformNightLimbasse.value,
                        uniformStartNight.value,
                        uniformEndNight.value
                    )
                    return { timestamp: index, ...v }
                })
                onChangePoints(next)
                return
            }
            const next = Array.from({ length: n }, (_, timestamp) => ({
                timestamp,
                consigne: Number(consigne.value),
                limhaute: Number(limhaute.value),
                limbasse: Number(limbasse.value),
            }))
            onChangePoints(next)
            return
        }

        if (n === 7) {
            const next = Array.from({ length: 7 }, (_, timestamp) => {
                const weekend = getIsLegacyWeekendDay(timestamp)
                const c = weekend ? weekendConsigne.value : weekConsigne.value
                const h = weekend ? weekendLimhaute.value : weekLimhaute.value
                const b = weekend ? weekendLimbasse.value : weekLimbasse.value
                return {
                    timestamp,
                    consigne: Number(c),
                    limhaute: Number(h),
                    limbasse: Number(b),
                }
            })
            onChangePoints(next)
            return
        }

        if (n === 168) {
            const next = Array.from({ length: 168 }, (_, index) => {
                const weekend = getIsLegacyWeekendHour(index)
                if (weekend) {
                    const v = getHourlyPointValues(
                        index,
                        weekendConsigne.value,
                        weekendLimhaute.value,
                        weekendLimbasse.value,
                        weekendIsNight.value,
                        weekendNightConsigne.value,
                        weekendNightLimhaute.value,
                        weekendNightLimbasse.value,
                        weekendStartNight.value,
                        weekendEndNight.value
                    )
                    return { timestamp: index, ...v }
                }
                const v = getHourlyPointValues(
                    index,
                    weekConsigne.value,
                    weekLimhaute.value,
                    weekLimbasse.value,
                    weekIsNight.value,
                    weekNightConsigne.value,
                    weekNightLimhaute.value,
                    weekNightLimbasse.value,
                    weekStartNight.value,
                    weekEndNight.value
                )
                return { timestamp: index, ...v }
            })
            onChangePoints(next)
        }
    }

    function collapseDistinguishToUniform() {
        consigne.value = weekConsigne.value
        limhaute.value = weekLimhaute.value
        limbasse.value = weekLimbasse.value
        isUniformNight.value = weekIsNight.value
        uniformNightConsigne.value = weekNightConsigne.value
        uniformNightLimhaute.value = weekNightLimhaute.value
        uniformNightLimbasse.value = weekNightLimbasse.value
        uniformStartNight.value = weekStartNight.value
        uniformEndNight.value = weekEndNight.value
    }

    function expandUniformToPeriods() {
        weekConsigne.value = consigne.value
        weekLimhaute.value = limhaute.value
        weekLimbasse.value = limbasse.value
        weekendConsigne.value = consigne.value
        weekendLimhaute.value = limhaute.value
        weekendLimbasse.value = limbasse.value

        weekIsNight.value = isUniformNight.value
        weekNightConsigne.value = uniformNightConsigne.value
        weekNightLimhaute.value = uniformNightLimhaute.value
        weekNightLimbasse.value = uniformNightLimbasse.value
        weekStartNight.value = uniformStartNight.value
        weekEndNight.value = uniformEndNight.value

        weekendIsNight.value = isUniformNight.value
        weekendNightConsigne.value = uniformNightConsigne.value
        weekendNightLimhaute.value = uniformNightLimhaute.value
        weekendNightLimbasse.value = uniformNightLimbasse.value
        weekendStartNight.value = uniformStartNight.value
        weekendEndNight.value = uniformEndNight.value
    }

    // ================================
    // WATCHERS
    // ================================
    watch(isDistinguishWeekend, (enabled, wasEnabled) => {
        if (suppressFormSideEffects.value) return
        if (enabled && wasEnabled === false) {
            expandUniformToPeriods()
        } else if (!enabled && wasEnabled === true) {
            collapseDistinguishToUniform()
        }
        rebuildAndEmitPoints()
    })

    watch(isUniformNight, (enabled, prev) => {
        if (suppressFormSideEffects.value) return
        if (enabled && prev === false) {
            copyUniformDayToNight()
        }
        rebuildAndEmitPoints()
    })

    watch(weekIsNight, (enabled, prev) => {
        if (suppressFormSideEffects.value) return
        if (enabled && prev === false) {
            copyWeekDayToNight()
        }
        rebuildAndEmitPoints()
    })

    watch(weekendIsNight, (enabled, prev) => {
        if (suppressFormSideEffects.value) return
        if (enabled && prev === false) {
            copyWeekendDayToNight()
        }
        rebuildAndEmitPoints()
    })

    watch([uniformStartNight, uniformEndNight], () => {
        if (suppressFormSideEffects.value) return
        if (getIsHourlySeries.value && isUniformNight.value) {
            rebuildAndEmitPoints()
        }
    })

    watch([weekStartNight, weekEndNight], () => {
        if (suppressFormSideEffects.value) return
        if (
            getIsHourlySeries.value &&
            weekIsNight.value &&
            isDistinguishWeekend.value
        ) {
            rebuildAndEmitPoints()
        }
    })

    watch([weekendStartNight, weekendEndNight], () => {
        if (suppressFormSideEffects.value) return
        if (
            getIsHourlySeries.value &&
            weekendIsNight.value &&
            isDistinguishWeekend.value
        ) {
            rebuildAndEmitPoints()
        }
    })

    watch(
        () => points.value.length,
        (len) => {
            if (len !== 168) {
                resetNightState()
            }
        }
    )

    /**
     * Reconstruit la série et notifie le parent à chaque changement de champ scalaire,
     * pour que le graphique (et le parent) se mettent à jour en direct pendant l’édition.
     * `suppressFormSideEffects` évite une boucle lorsque les points sont réinjectés depuis le parent.
     */
    watch(
        [
            consigne,
            limhaute,
            limbasse,
            weekConsigne,
            weekLimhaute,
            weekLimbasse,
            weekendConsigne,
            weekendLimhaute,
            weekendLimbasse,
            isUniformNight,
            uniformNightConsigne,
            uniformNightLimhaute,
            uniformNightLimbasse,
            uniformStartNight,
            uniformEndNight,
            weekIsNight,
            weekNightConsigne,
            weekNightLimhaute,
            weekNightLimbasse,
            weekStartNight,
            weekEndNight,
            weekendIsNight,
            weekendNightConsigne,
            weekendNightLimhaute,
            weekendNightLimbasse,
            weekendStartNight,
            weekendEndNight,
        ],
        () => {
            if (suppressFormSideEffects.value) return
            rebuildAndEmitPoints()
        }
    )

    watch(
        () => points.value,
        (pts) => {
            if (pts.length === 0) {
                inferredForPointsLength.value = null
            } else if (inferredForPointsLength.value !== pts.length) {
                suppressFormSideEffects.value = true
                try {
                    const inferred = inferAlarmThresholdTogglesFromPoints(pts)
                    isDistinguishWeekend.value = inferred.isDistinguishWeekend
                    isUniformNight.value = inferred.isUniformNight
                    uniformStartNight.value = inferred.uniformStartNight
                    uniformEndNight.value = inferred.uniformEndNight
                    weekIsNight.value = inferred.weekIsNight
                    weekStartNight.value = inferred.weekStartNight
                    weekEndNight.value = inferred.weekEndNight
                    weekendIsNight.value = inferred.weekendIsNight
                    weekendStartNight.value = inferred.weekendStartNight
                    weekendEndNight.value = inferred.weekendEndNight
                    inferredForPointsLength.value = pts.length
                } finally {
                    /** Après le flush des watchers (ex. `isDistinguishWeekend` → `expandUniformToPeriods`). */
                    void nextTick(() => {
                        suppressFormSideEffects.value = false
                    })
                }
            }
            suppressFormSideEffects.value = true
            try {
                syncScalarsFromPoints(pts)
            } finally {
                void nextTick(() => {
                    suppressFormSideEffects.value = false
                })
            }
        },
        { deep: true, immediate: true }
    )

    return {
        isDistinguishWeekend,
        consigne,
        limhaute,
        limbasse,
        weekConsigne,
        weekLimhaute,
        weekLimbasse,
        weekendConsigne,
        weekendLimhaute,
        weekendLimbasse,
        isUniformNight,
        uniformNightConsigne,
        uniformNightLimhaute,
        uniformNightLimbasse,
        uniformStartNight,
        uniformEndNight,
        weekIsNight,
        weekNightConsigne,
        weekNightLimhaute,
        weekNightLimbasse,
        weekStartNight,
        weekEndNight,
        weekendIsNight,
        weekendNightConsigne,
        weekendNightLimhaute,
        weekendNightLimbasse,
        weekendStartNight,
        weekendEndNight,
        getPointsLength,
        getIsHourlySeries,
        getShowDistinguishToggle,
        getStartNightSelectOptions,
        getEndNightSelectOptions,
        getIsValidThresholdForm,
        rebuildAndEmitPoints,
    }
}
